import type { ComponentType } from 'react';
import type { Permission } from '@backstage/plugin-permission-common';
import { agentAdminPermission } from '@platform/plugin-platform-common';
import { keycloakAdminPermission } from '@platform/plugin-keycloak-admin-common';
import { notificationsAdminPermission } from '@platform/plugin-slack-notifications-common';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import CallMergeIcon from '@material-ui/icons/CallMerge';
import SecurityIcon from '@material-ui/icons/Security';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import TimelineIcon from '@material-ui/icons/Timeline';
import PolicyIcon from '@material-ui/icons/Policy';
import ViewListIcon from '@material-ui/icons/ViewList';
import BuildIcon from '@material-ui/icons/Build';
import ListAltIcon from '@material-ui/icons/ListAlt';

/** Entrée statique : chemin écrit ici. */
export interface NavItem {
  to: string;
  text: string;
  icon: ComponentType<any>;
  /** Actif (et ouvre son groupe) seulement sur cette adresse exacte. */
  exact?: boolean;
}

/**
 * Entrée portée par un plugin (`page:<pluginId>[/<nom>]`) : titre, icône et
 * chemin viennent de sa page. Si le plugin n'est pas installé, l'entrée
 * disparaît d'elle-même.
 */
export interface PluginNavItem {
  take: string;
  /** Entrée masquée sans cette permission (modules d'administration). */
  permission?: Permission;
  /** Libellé à la place du titre de la page (plugins amont en anglais). */
  text?: string;
  icon?: ComponentType<any>;
  /** Actif (et ouvre son groupe) seulement sur cette adresse exacte. */
  exact?: boolean;
}

export type NavSectionItem = NavItem | PluginNavItem;

/** Groupe repliable dans une section (maquette : « Accès & administration », « Catalogue & outils »). */
export interface NavGroup {
  group: string;
  icon: ComponentType<any>;
  items: NavSectionItem[];
}

export const isPluginNavItem = (item: NavSectionItem | NavGroup): item is PluginNavItem =>
  'take' in item;

export const isNavGroup = (item: NavSectionItem | NavGroup): item is NavGroup => 'group' in item;

export interface NavSection {
  id: string;
  label: string;
  items: Array<NavSectionItem | NavGroup>;
  collapsedByDefault?: boolean;
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'pilotage',
    label: 'Pilotage',
    items: [
      { to: 'mon-travail', text: 'Mon travail', icon: AssignmentTurnedInIcon },
      { to: 'merge-requests', text: 'Mes merge requests', icon: CallMergeIcon },
      { take: 'page:incidents' },
      // Page du plugin dora sans titre de navigation (sinon barre de titre en doublon) : lien direct.
      { to: 'dora', text: 'Performance DORA', icon: TimelineIcon },
    ],
  },
  {
    id: 'gouvernance',
    label: 'Gouvernance',
    items: [
      {
        group: 'Accès & administration',
        icon: SecurityIcon,
        items: [
          { to: 'acces-et-roles', text: 'Accès & rôles', icon: SecurityIcon },
          { take: 'page:keycloak-admin', permission: keycloakAdminPermission, text: 'Utilisateurs & groupes' },
          { take: 'page:agent-router', permission: agentAdminPermission, text: 'Clusters & agents' },
          { take: 'page:slack-notifications', permission: notificationsAdminPermission, text: 'Notifications Slack' },
          { take: 'page:rbac', text: 'Rôles (avancé)', icon: VerifiedUserIcon },
          { to: 'rbac-catalogue', text: 'Catalogue des droits', icon: PolicyIcon },
          // Absent de la maquette, conservé : aucune fonction retirée.
          { take: 'page:cost', text: 'Coûts & FinOps' },
        ],
      },
      {
        group: 'Catalogue & outils',
        icon: BuildIcon,
        items: [
          // Exact : les fiches (/catalog/…) ne doivent pas ouvrir ce groupe.
          { take: 'page:catalog', text: 'Catalogue logiciel', icon: ViewListIcon, exact: true },
          { to: 'outils', text: 'Tous les outils', icon: ListAltIcon },
        ],
      },
    ],
  },
];

/** Entrées d'une section, groupes aplatis (pour prélever les pages de plugins). */
export const allItems = (section: NavSection): NavSectionItem[] =>
  section.items.flatMap(i => (isNavGroup(i) ? i.items : [i]));

export const HIDDEN_PAGES = [
  'page:home',
  'page:api-docs',
  'page:catalog-graph',
  'page:techdocs',
  'page:catalog-import',
  'page:app-visualizer',
  'page:search',
  'page:notifications',
  'page:user-settings',
  'page:dora',
  // Retirée du menu (création de dépôt) ; toujours joignable par son adresse.
  'page:scaffolder',
  // Pages de l'app sans entrée automatique (pas d'icône) ; prélevées par sécurité.
  'page:app/partners',
  'page:app/tools',
];
